/**
 * EntityDetailPanel — Advanced entity inspection panel
 *
 * Full-featured panel supporting:
 * - 13 tabs (auto-derived from tabRegistry.ts — add new tabs there)
 * - Full-screen expand mode
 * - Dock positions (right / bottom)
 * - Size presets (compact / standard / wide / fullscreen)
 * - Arrow-key row navigation
 * - Quick actions bar
 * - Breadcrumb context
 * - Keyboard shortcuts
 * - Tab-level error boundaries
 * - ARIA accessibility
 *
 * DUMB FRONTEND: All data comes from backend via GraphQL.
 * This component ONLY displays what backend defines.
 */

import { useMemo } from 'react';
import {
  Drawer,
  Tabs,
  Text,
  Group,
  ScrollArea,
  Table,
  Alert,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconMaximize,
  IconMinimize,
  IconLayoutBottombar,
  IconLayoutSidebar,
  IconChevronUp,
  IconChevronDown,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconLayoutNavbar,
  IconLayoutSidebarRight,
} from '@tabler/icons-react';
import { getPrimitiveMetadata } from '@/config/primitive-metadata';
import { formatFieldName, FieldValue, INTERNAL_ID_FIELDS } from '@/components/RecordDetailDrawer';
import { EntityMediaHeader } from '@/components/shared/EntityMediaHeader';
import type {
  EntityDetailPanelProps,
  DetailTab,
  RowNavigationState,
  BreadcrumbSegment,
} from './types';
import {
  TAB_ICONS,
  TAB_LABELS,
  getVisibleTabs,
  PANEL_SIZE_MAP,
  BOTTOM_DOCK_HEIGHT,
} from './types';
import { useEntityDetailPanel } from './useEntityDetailPanel';
import { AuditTrailTab } from './tabs/AuditTrailTab';
import { RelationshipsTab } from './tabs/RelationshipsTab';
import { TimelineTab } from './tabs/TimelineTab';
import { VersionsTab } from './tabs/VersionsTab';
import { QuickActionsBar } from './QuickActionsBar';
import { WorkflowContextTab } from './tabs/WorkflowContextTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { NotesTab } from './tabs/NotesTab';
import { CompareTab } from './tabs/CompareTab';
import { TaskScheduleTab } from './tabs/TaskScheduleTab';
import { TaskFormTab } from './tabs/TaskFormTab';
import { WorkflowFlowTab } from './tabs/WorkflowFlowTab';
import { TabErrorBoundary } from './TabErrorBoundary';
import { useDetailKeyboardShortcuts } from './useDetailKeyboardShortcuts';
import { ShortcutOverlay } from './ShortcutOverlay';
import { useFormSchemaViews } from './useFormSchemaViews';
import { getTabRenderer, type TabRendererProps } from '@ferp/workflow-forms';

// ============================================================================
// STATUS COLOR MAP (same as TabulatorPrimitiveManager)
// ============================================================================

const STATUS_COLOR_MAP: Record<string, string> = {
  ACTIVE: 'green',
  PENDING: 'yellow',
  COMPLETED: 'green',
  FAILED: 'red',
  ARCHIVED: 'gray',
  DRAFT: 'blue',
  PAUSED: 'gray',
  IN_PROGRESS: 'blue',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function EntityDetailPanel({
  entity,
  primitiveType,
  opened,
  onClose,
  isReadOnly = false,
  onEntityUpdated,
  navigation,
  breadcrumbs,
  editContent,
  detailsContent,
  statusColorMap,
  allEntities,
  tabContentOverrides,
  extraQuickActions,
  // Deprecated props — forward to tabContentOverrides
  dependenciesContent,
  workflowMapContent,
}: EntityDetailPanelProps) {
  const panel = useEntityDetailPanel();
  const metadata = getPrimitiveMetadata(primitiveType);
  const PrimitiveIcon = metadata.icon as React.ComponentType<{ size: number }>;
  const colorMap = statusColorMap ?? STATUS_COLOR_MAP;

  // Merge deprecated props into tabContentOverrides
  const overrides = useMemo(() => ({
    ...tabContentOverrides,
    ...(dependenciesContent && !tabContentOverrides?.dependencies
      ? { dependencies: dependenciesContent } : {}),
    ...(workflowMapContent && !tabContentOverrides?.['workflow-map']
      ? { 'workflow-map': workflowMapContent } : {}),
  }), [tabContentOverrides, dependenciesContent, workflowMapContent]);

  // DRR-165: Load FormSchema-driven tabs (if views section exists)
  const { tabs: formSchemaTabs, tabConfigs } = useFormSchemaViews(primitiveType);

  // Determine which tabs are visible — FormSchema views EXTEND defaults (not replace)
  const visibleTabs = useMemo<DetailTab[]>(() => {
    const defaults = getVisibleTabs(primitiveType);
    if (!formSchemaTabs) return defaults;
    // Merge: start with defaults, then add any FormSchema tabs not already present
    const defaultSet = new Set(defaults);
    const extra = formSchemaTabs
      .filter(t => !defaultSet.has(t.id))
      .map(t => t.id);
    return [...defaults, ...extra];
  }, [primitiveType, formSchemaTabs]);

  // Tab labels and icons — merge FormSchema tabs with defaults
  const tabLabels = useMemo(() => {
    if (!formSchemaTabs) return TAB_LABELS;
    const merged = { ...TAB_LABELS };
    for (const t of formSchemaTabs) {
      merged[t.id] = t.label;
    }
    return merged;
  }, [formSchemaTabs]);

  const tabIcons = useMemo(() => {
    if (!formSchemaTabs) return TAB_ICONS;
    const merged = { ...TAB_ICONS };
    for (const t of formSchemaTabs) {
      merged[t.id] = t.icon;
    }
    return merged;
  }, [formSchemaTabs]);

  // Keyboard shortcuts
  const shortcuts = useDetailKeyboardShortcuts({
    panelOpened: opened,
    visibleTabs,
    setActiveTab: panel.setActiveTab,
    toggleFullscreen: panel.toggleFullscreen,
    isFullscreen: panel.isFullscreen,
    onClose,
  });

  // Scroll area height adapts to fullscreen vs drawer
  const scrollHeight = panel.isFullscreen
    ? 'calc(100vh - 180px)'
    : 'calc(100vh - 200px)';

  // Drawer size based on panel state
  const drawerSize = panel.isFullscreen
    ? '100%'
    : PANEL_SIZE_MAP[panel.panelSize === 'fullscreen' ? 'wide' : panel.panelSize] ?? 'lg';

  const entityName = (entity?.name as string) ?? metadata.name;

  return (
  <>
    <Drawer
      opened={opened}
      onClose={() => {
        onClose();
        if (panel.isFullscreen) panel.toggleFullscreen();
      }}
      position={panel.isFullscreen ? 'right' : panel.dockPosition === 'bottom' ? 'bottom' : 'right'}
      size={panel.dockPosition === 'bottom' && !panel.isFullscreen
        ? BOTTOM_DOCK_HEIGHT[panel.panelSize === 'fullscreen' ? 'wide' : panel.panelSize] ?? '45vh'
        : drawerSize
      }
      zIndex={1100}
      aria-label={`Entity detail panel for ${entityName}`}
      title={
        <EntityMediaHeader
          name={entityName}
          subtitle={entity?.description as string | undefined}
          status={entity?.status as string | undefined}
          statusColorMap={colorMap}
          safetyLevel={entity?.safetyLevel as string | undefined}
          imageUrl={entity?.profileImageUrl as string | undefined}
          accentColor={metadata.phaseColor}
          Icon={PrimitiveIcon}
          entityId={entity?.id as string | undefined}
          isFullscreen={panel.isFullscreen}
        />
      }
    >
      {/* Toolbar: navigation, dock, size, fullscreen */}
      <EntityDetailToolbar
        panel={panel}
        navigation={navigation}
      />

      {/* Breadcrumb trail */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <BreadcrumbBar segments={breadcrumbs} />
      )}

      {/* Tab navigation */}
      <Tabs
        value={panel.activeTab}
        onChange={(val) => panel.setActiveTab(val as DetailTab)}
        aria-label="Entity detail tabs"
      >
        <Tabs.List role="tablist" aria-label="Entity inspection tabs">
          {visibleTabs.map(tab => {
            const TabIcon = tabIcons[tab] ?? TAB_ICONS.details;
            const isDisabled = tab === 'edit' && isReadOnly;
            return (
              <Tabs.Tab
                key={tab}
                value={tab}
                leftSection={<TabIcon size={14} />}
                disabled={isDisabled}
                aria-label={tabLabels[tab] ?? tab}
              >
                {tabLabels[tab] ?? tab}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>

        {/* Details Tab — layout flips between vertical rows and horizontal columns */}
        <Tabs.Panel value="details" pt="md" role="tabpanel" aria-label="Entity details">
          <TabErrorBoundary tabName="Details">
            {detailsContent ?? (
              entity && (
                panel.tabOrientation === 'horizontal' ? (
                  /* Horizontal: fields as column headers, values in row, scroll left-right */
                  <ScrollArea h={scrollHeight} type="auto">
                    <Table striped highlightOnHover withTableBorder aria-label="Entity field values (horizontal)">
                      <Table.Thead>
                        <Table.Tr>
                          {Object.entries(entity)
                            .filter(([key, val]) => !key.startsWith('_') && key !== '__typename' && !INTERNAL_ID_FIELDS.has(key) && !Array.isArray(val))
                            .map(([key]) => (
                              <Table.Th key={key} style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                                {formatFieldName(key)}
                              </Table.Th>
                            ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        <Table.Tr>
                          {Object.entries(entity)
                            .filter(([key, val]) => !key.startsWith('_') && key !== '__typename' && !INTERNAL_ID_FIELDS.has(key) && !Array.isArray(val))
                            .map(([key, value]) => (
                              <Table.Td key={key} style={{ whiteSpace: 'nowrap' }}>
                                <FieldValue value={value} fieldKey={key} entity={entity} />
                              </Table.Td>
                            ))}
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                ) : (
                  /* Vertical (default): label-value rows stacked down */
                  <ScrollArea h={scrollHeight}>
                    <Table striped highlightOnHover withTableBorder aria-label="Entity field values">
                      <Table.Tbody>
                        {Object.entries(entity)
                          .filter(([key, val]) => !key.startsWith('_') && key !== '__typename' && !INTERNAL_ID_FIELDS.has(key) && !Array.isArray(val))
                          .map(([key, value]) => (
                            <Table.Tr key={key}>
                              <Table.Td fw={600} w={140} style={{ textTransform: 'capitalize' }}>
                                {formatFieldName(key)}
                              </Table.Td>
                              <Table.Td>
                                <FieldValue value={value} fieldKey={key} entity={entity} />
                              </Table.Td>
                            </Table.Tr>
                          ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                )
              )
            )}
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Edit Tab */}
        <Tabs.Panel value="edit" pt="md" role="tabpanel" aria-label="Edit entity">
          <TabErrorBoundary tabName="Edit">
            <ScrollArea h={scrollHeight}>
              {entity && !isReadOnly ? (
                editContent ?? (
                  <Alert color="blue" title="Edit Mode">
                    Edit form will be rendered here via PrimitiveFormModal inline.
                  </Alert>
                )
              ) : (
                <Alert color="yellow" title="Read Only">
                  Mutations are not available for {metadata.name}. This primitive is read-only.
                </Alert>
              )}
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Relationships Tab — live typed edges via configEdges/configReverseEdges (DRR-110, Gap #5) */}
        <Tabs.Panel value="relationships" pt="md" role="tabpanel" aria-label="Entity relationships">
          <TabErrorBoundary tabName="Relationships">
            <ScrollArea h={scrollHeight}>
              <RelationshipsTab entity={entity} primitiveType={primitiveType} />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Audit Tab — entity-scoped audit trail */}
        <Tabs.Panel value="audit" pt="md" role="tabpanel" aria-label="Audit trail">
          <TabErrorBoundary tabName="Audit Trail">
            <ScrollArea h={scrollHeight}>
              <AuditTrailTab entity={entity} primitiveType={primitiveType} />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Timeline Tab — visual status progression */}
        <Tabs.Panel value="timeline" pt="md" role="tabpanel" aria-label="Status timeline">
          <TabErrorBoundary tabName="Timeline">
            <ScrollArea h={scrollHeight}>
              <TimelineTab entity={entity} primitiveType={primitiveType} />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Versions Tab — version history and diff */}
        <Tabs.Panel value="versions" pt="md" role="tabpanel" aria-label="Version history">
          <TabErrorBoundary tabName="Versions">
            <ScrollArea h={scrollHeight}>
              <VersionsTab entity={entity} primitiveType={primitiveType} />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Context Tab — workflow position for task/instance/approval/checkpoint */}
        {visibleTabs.includes('context') && (
          <Tabs.Panel value="context" pt="md" role="tabpanel" aria-label="Workflow context">
            <TabErrorBoundary tabName="Workflow Context">
              <ScrollArea h={scrollHeight}>
                <WorkflowContextTab entity={entity} primitiveType={primitiveType} />
              </ScrollArea>
            </TabErrorBoundary>
          </Tabs.Panel>
        )}

        {/* Task Form Tab — renders composable form fragments (DRR-173) or legacy formSchemaRef */}
        {primitiveType === 'task' && entity && (
          (entity as Record<string, unknown>).formSchemaRef ||
          ((entity as Record<string, unknown>).formComponents as string[] | undefined)?.length
        ) && (
          <Tabs.Panel value="form" pt="md" role="tabpanel" aria-label="Task form">
            <TabErrorBoundary tabName="Form">
              <ScrollArea h={scrollHeight}>
                <TaskFormTab
                  entity={entity as Record<string, unknown>}
                  tenantId=""
                  primitiveType="task"
                />
              </ScrollArea>
            </TabErrorBoundary>
          </Tabs.Panel>
        )}

        {/* Schedule Tab — FullCalendar weekly grid for task scheduling */}
        {visibleTabs.includes('schedule') && (
          <Tabs.Panel value="schedule" pt="md" role="tabpanel" aria-label="Task schedule">
            <TabErrorBoundary tabName="Schedule">
              <ScrollArea h={scrollHeight}>
                <TaskScheduleTab
                  entity={entity}
                  primitiveType={primitiveType}
                  siblingTasks={allEntities}
                />
              </ScrollArea>
            </TabErrorBoundary>
          </Tabs.Panel>
        )}

        {/* Dependencies Tab — content injected via tabContentOverrides */}
        {visibleTabs.includes('dependencies') && (
          <Tabs.Panel value="dependencies" pt="md" role="tabpanel" aria-label="Task dependencies">
            <TabErrorBoundary tabName="Dependencies">
              <ScrollArea h={scrollHeight}>
                {overrides.dependencies ?? (
                  <Alert color="blue" title="Dependencies">
                    No dependency visualization available for this entity type.
                  </Alert>
                )}
              </ScrollArea>
            </TabErrorBoundary>
          </Tabs.Panel>
        )}

        {/* Workflow Map Tab — content injected via tabContentOverrides */}
        {visibleTabs.includes('workflow-map') && (
          <Tabs.Panel value="workflow-map" pt="md" role="tabpanel" aria-label="Workflow map">
            <TabErrorBoundary tabName="Workflow Map">
              <ScrollArea h={scrollHeight}>
                {overrides['workflow-map'] ?? (
                  <Alert color="blue" title="Workflow Map">
                    No workflow map available for this entity type.
                  </Alert>
                )}
              </ScrollArea>
            </TabErrorBoundary>
          </Tabs.Panel>
        )}

        {/* Flow Tab — workflow state machine visualization (workflow only) */}
        {visibleTabs.includes('flow') && (
          <Tabs.Panel value="flow" pt="md" role="tabpanel" aria-label="State machine flow">
            <TabErrorBoundary tabName="Flow">
              <WorkflowFlowTab entity={entity} primitiveType={primitiveType} />
            </TabErrorBoundary>
          </Tabs.Panel>
        )}

        {/* Documents Tab */}
        <Tabs.Panel value="documents" pt="md" role="tabpanel" aria-label="Entity documents">
          <TabErrorBoundary tabName="Documents">
            <ScrollArea h={scrollHeight}>
              <DocumentsTab entity={entity} primitiveType={primitiveType} />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Notes Tab */}
        <Tabs.Panel value="notes" pt="md" role="tabpanel" aria-label="Entity notes">
          <TabErrorBoundary tabName="Notes">
            <ScrollArea h={scrollHeight}>
              <NotesTab entity={entity} primitiveType={primitiveType} />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* Compare Tab — side-by-side entity comparison */}
        <Tabs.Panel value="compare" pt="md" role="tabpanel" aria-label="Compare entities">
          <TabErrorBoundary tabName="Compare">
            <ScrollArea h={scrollHeight}>
              <CompareTab
                entity={entity}
                primitiveType={primitiveType}
                allEntities={allEntities}
              />
            </ScrollArea>
          </TabErrorBoundary>
        </Tabs.Panel>

        {/* DRR-165: FormSchema-driven tabs via TabRendererRegistry */}
        {tabConfigs?.map(cfg => {
          // Skip tabs that already have hardcoded panels above
          const hardcodedTabs = new Set([
            'details', 'edit', 'relationships', 'audit', 'timeline',
            'versions', 'context', 'schedule', 'dependencies',
            'workflow-map', 'flow', 'documents', 'notes', 'compare',
          ]);
          if (hardcodedTabs.has(cfg.id)) return null;

          const Renderer = getTabRenderer(cfg.renderer);
          return (
            <Tabs.Panel key={cfg.id} value={cfg.id} pt="md" role="tabpanel" aria-label={cfg.label}>
              <TabErrorBoundary tabName={cfg.label}>
                <ScrollArea h={scrollHeight}>
                  {Renderer && entity ? (
                    <Renderer
                      entity={entity as Record<string, unknown>}
                      tenantId=""
                      primitiveType={primitiveType}
                      tabProps={cfg.props}
                    />
                  ) : (
                    <Alert color="blue" title={cfg.label}>
                      Renderer &apos;{cfg.renderer}&apos; not registered. Register via registerTabRenderer().
                    </Alert>
                  )}
                </ScrollArea>
              </TabErrorBoundary>
            </Tabs.Panel>
          );
        })}
      </Tabs>

      {/* Quick Actions Bar — always visible at bottom */}
      <QuickActionsBar
        entity={entity}
        primitiveType={primitiveType}
        isReadOnly={isReadOnly}
        onAction={(actionId, targetStatus) => {
          // TODO: Wire to actual mutation handlers via onEntityUpdated callback
          console.log(`Quick action: ${actionId} → ${targetStatus}`);
          onEntityUpdated?.();
        }}
        extraActions={extraQuickActions}
      />
    </Drawer>

    {/* Keyboard shortcut help overlay (? key) */}
    <ShortcutOverlay opened={shortcuts.showOverlay} onClose={shortcuts.closeOverlay} />
  </>
  );
}

// ============================================================================
// TOOLBAR
// ============================================================================

interface EntityDetailToolbarProps {
  panel: ReturnType<typeof useEntityDetailPanel>;
  navigation?: RowNavigationState;
}

function EntityDetailToolbar({ panel, navigation }: EntityDetailToolbarProps) {
  return (
    <Group justify="space-between" mb="sm" role="toolbar" aria-label="Panel controls">
      {/* Left: navigation */}
      <Group gap="xs" role="group" aria-label="Row navigation">
        {navigation && (
          <>
            <Tooltip label="Previous row (Arrow Up)">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={navigation.goToPrevious}
                disabled={navigation.currentIndex <= 0}
                aria-label="Previous row"
              >
                <IconChevronUp size={16} />
              </ActionIcon>
            </Tooltip>
            <Text size="xs" c="dimmed" aria-live="polite">
              {navigation.currentIndex + 1} of {navigation.totalRows}
            </Text>
            <Tooltip label="Next row (Arrow Down)">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={navigation.goToNext}
                disabled={navigation.currentIndex >= navigation.totalRows - 1}
                aria-label="Next row"
              >
                <IconChevronDown size={16} />
              </ActionIcon>
            </Tooltip>
          </>
        )}
      </Group>

      {/* Right: dock, size, fullscreen */}
      <Group gap="xs" role="group" aria-label="Panel layout controls">
        {/* Record layout toggle: vertical rows vs horizontal columns */}
        <Tooltip label={panel.tabOrientation === 'vertical' ? 'Horizontal record (scroll sideways)' : 'Vertical record (scroll down)'}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={panel.toggleTabOrientation}
            aria-label={panel.tabOrientation === 'vertical' ? 'Switch to horizontal scrolling record' : 'Switch to vertical record rows'}
          >
            {panel.tabOrientation === 'vertical'
              ? <IconLayoutSidebarRight size={16} />
              : <IconLayoutNavbar size={16} />
            }
          </ActionIcon>
        </Tooltip>

        {/* Dock position toggle */}
        <Tooltip label={panel.dockPosition === 'right' ? 'Dock to bottom' : 'Dock to right'}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => panel.setDockPosition(panel.dockPosition === 'right' ? 'bottom' : 'right')}
            aria-label={panel.dockPosition === 'right' ? 'Dock panel to bottom' : 'Dock panel to right'}
          >
            {panel.dockPosition === 'right'
              ? <IconLayoutBottombar size={16} />
              : <IconLayoutSidebar size={16} />
            }
          </ActionIcon>
        </Tooltip>

        {/* Size cycle: compact → standard → wide */}
        <Tooltip label={`Size: ${panel.panelSize}`}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => {
              const sizes = ['compact', 'standard', 'wide'] as const;
              const currentIdx = sizes.indexOf(panel.panelSize as typeof sizes[number]);
              const nextIdx = (currentIdx + 1) % sizes.length;
              panel.setPanelSize(sizes[nextIdx] ?? 'standard');
            }}
            aria-label={`Panel size: ${panel.panelSize}. Click to cycle.`}
          >
            {panel.panelSize === 'compact'
              ? <IconArrowsMinimize size={16} />
              : <IconArrowsMaximize size={16} />
            }
          </ActionIcon>
        </Tooltip>

        {/* Fullscreen toggle */}
        <Tooltip label={panel.isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen (F)'}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={panel.toggleFullscreen}
            aria-label={panel.isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
          >
            {panel.isFullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}

// ============================================================================
// BREADCRUMB BAR
// ============================================================================

function BreadcrumbBar({ segments }: { segments: BreadcrumbSegment[] }) {
  return (
    <Group gap={4} mb="xs" role="navigation" aria-label="Entity breadcrumb">
      {segments.map((seg, idx) => (
        <Group key={idx} gap={4}>
          {idx > 0 && <Text size="xs" c="dimmed" aria-hidden>/</Text>}
          <Text
            size="xs"
            c={seg.onClick ? 'blue' : 'dimmed'}
            style={seg.onClick ? { cursor: 'pointer' } : undefined}
            onClick={seg.onClick}
            role={seg.onClick ? 'link' : undefined}
            tabIndex={seg.onClick ? 0 : undefined}
            onKeyDown={seg.onClick ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                seg.onClick?.();
              }
            } : undefined}
            aria-current={idx === segments.length - 1 ? 'page' : undefined}
          >
            {seg.label}
          </Text>
        </Group>
      ))}
    </Group>
  );
}


export default EntityDetailPanel;
